import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MultiSelectEmployee from '../../../components/ui/MultiSelectEmployee';
import IncentiveSplitConfig from '../../../components/ui/IncentiveSplitConfig';
import { fetchEmployees, checkTicketExistsForCalculation, markAsCloseWonAndCreateTicket } from '../../../services/incentiveTicketsService';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const CloseWonModal = ({ isOpen, onClose, estimate, calculations }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingTicket, setExistingTicket] = useState(null);
  const [checkingTicket, setCheckingTicket] = useState(false);

  // Multi-select state
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [splitConfig, setSplitConfig] = useState({ method: 'equal', shares: {} });

  const [form, setForm] = useState({
    wonDate: new Date()?.toISOString()?.split('T')?.[0],
    clientPoReference: '',
    finalProjectValue: '',
    finalTargetMarginAmount: '',
    dealNotes: '',
  });

  useEffect(() => {
    if (isOpen && estimate?.id) {
      loadEmployees();
      checkExistingTicket();
      setSelectedEmployees([]);
      setSplitConfig({ method: 'equal', shares: {} });
      setForm(prev => ({
        ...prev,
        finalProjectValue: String(calculations?.sellPrice || estimate?.calculations?.sellPrice || ''),
        finalTargetMarginAmount: String(calculations?.grossProfit || estimate?.calculations?.grossProfit || ''),
      }));
    }
  }, [isOpen, estimate?.id]);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const data = await fetchEmployees();
      setEmployees(data);
    } catch (err) {
      console.error('[CloseWonModal] Load employees error:', err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const checkExistingTicket = async () => {
    if (!estimate?.id) return;
    setCheckingTicket(true);
    try {
      const ticket = await checkTicketExistsForCalculation(estimate?.id);
      setExistingTicket(ticket);
    } catch (err) {
      console.error('[CloseWonModal] Check ticket error:', err);
    } finally {
      setCheckingTicket(false);
    }
  };

  const handleEmployeesChange = (emps) => {
    setSelectedEmployees(emps);
    // Reset split config when employees change
    setSplitConfig({ method: 'equal', shares: {} });
  };

  // Validate split config
  const isSplitValid = () => {
    if (selectedEmployees?.length === 0) return false;
    if (selectedEmployees?.length === 1) return true;
    if (splitConfig?.method === 'equal') return true;
    const total = Object.values(splitConfig?.shares)?.reduce((s, v) => s + (parseFloat(v) || 0), 0);
    return Math.abs(total - 100) < 0.01;
  };

  const handleSubmit = async () => {
    setError('');

    if (selectedEmployees?.length === 0) { setError('Please select at least one Sales Deal Owner.'); return; }
    if (!form?.wonDate) { setError('Please enter the Won Date.'); return; }
    const finalMargin = parseFloat(form?.finalTargetMarginAmount) || 0;
    if (finalMargin <= 0) { setError('Final Target Margin Amount must be greater than 0.'); return; }
    if (!isSplitValid()) { setError('Incentive split percentages must total 100%.'); return; }

    setSubmitting(true);
    try {
      // Build per-employee split data
      const salesIncentive = finalMargin * 0.02;
      let employeeSplits;
      if (selectedEmployees?.length === 1) {
        employeeSplits = [{ employee: selectedEmployees?.[0], percentage: 100, amount: salesIncentive }];
      } else if (splitConfig?.method === 'equal') {
        let pct = 100 / selectedEmployees?.length;
        employeeSplits = selectedEmployees?.map((emp, i) => ({
          employee: emp,
          percentage: i === selectedEmployees?.length - 1
            ? parseFloat((100 - pct * (selectedEmployees?.length - 1))?.toFixed(2))
            : parseFloat(pct?.toFixed(2)),
          amount: salesIncentive / selectedEmployees?.length,
        }));
      } else {
        employeeSplits = selectedEmployees?.map(emp => {
          let pct = parseFloat(splitConfig?.shares?.[emp?.id]) || 0;
          return { employee: emp, percentage: pct, amount: salesIncentive * (pct / 100) };
        });
      }

      const closeWonData = {
        // Primary owner = first selected employee (for backward compat)
        // Strip "salary-" prefix for salary-group employees — sales_deal_owner_id is a UUID column
        salesDealOwnerId: (() => {
          const rawId = selectedEmployees?.[0]?.id;
          if (!rawId) return null;
          if (typeof rawId === 'string' && rawId?.startsWith('salary-')) {
            return rawId?.replace(/^salary-/, '') || null;
          }
          return rawId;
        })(),
        salesDealOwnerName: selectedEmployees?.[0]?.fullName || '',
        salesDealOwnerDepartment: selectedEmployees?.[0]?.department || 'General',
        salesDealOwnerRole: selectedEmployees?.[0]?.role || 'Employee',
        // Multi-owner data
        salesDealOwners: selectedEmployees,
        employeeSplits,
        splitMethod: selectedEmployees?.length === 1 ? 'SALES_OWNER_100' : (splitConfig?.method === 'equal' ? 'EQUAL' : 'CUSTOM'),
        // Form fields
        wonDate: form?.wonDate,
        clientPoReference: form?.clientPoReference,
        finalProjectValue: parseFloat(form?.finalProjectValue) || 0,
        finalTargetMarginAmount: finalMargin,
        dealNotes: form?.dealNotes,
        projectName: estimate?.name || 'Unnamed Project',
        clientName: estimate?.ptcNumber || estimate?.ptc_number || '',
        projectValue: parseFloat(estimate?.calculations?.sellPrice) || 0,
        totalCost: parseFloat(estimate?.calculations?.totalCost) || 0,
        targetMarginPercentage: parseFloat(estimate?.config?.targetMargin) || 0,
        targetMarginAmount: parseFloat(estimate?.calculations?.grossProfit) || 0,
        wonBy: user?.profile?.full_name || user?.email || '',
      };

      const ticket = await markAsCloseWonAndCreateTicket(estimate?.id, closeWonData, user?.id);
      onClose({ success: true, ticket });
      navigate(`/incentive-tickets/${ticket?.id}`);
    } catch (err) {
      console.error('[CloseWonModal] Submit error:', err);
      setError(err?.message || 'Failed to create incentive ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatOMR = (v) => `OMR ${(parseFloat(v) || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;

  const finalMargin = parseFloat(form?.finalTargetMarginAmount) || 0;
  const salesIncentive = finalMargin * 0.02;
  const projectIncentive = finalMargin * 0.02;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-success to-emerald-600 shadow-lg">
              <Icon name="Trophy" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Mark as Close Won & Create Incentive Ticket</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{estimate?.name}</p>
            </div>
          </div>
          <button onClick={() => onClose(null)} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {checkingTicket ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : existingTicket ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="p-4 rounded-2xl bg-success/10 border border-success/30">
                <Icon name="CheckCircle" size={40} className="text-success mx-auto" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Incentive Ticket Already Exists</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Ticket <span className="font-mono font-bold text-primary">{existingTicket?.ticketNumber}</span> was already created for this calculation.
                </p>
                <p className="text-xs text-muted-foreground mt-1">Status: {existingTicket?.status}</p>
              </div>
              <Button
                variant="default"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={() => { onClose(null); navigate(`/incentive-tickets/${existingTicket?.id}`); }}
              >
                View Incentive Ticket
              </Button>
            </div>
          ) : (
            <>
              {error && (
                <div className="flex items-start gap-3 p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
                  <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Sales Deal Owner — Multi-Select */}
              <div>
                <label className="text-sm font-semibold text-foreground block mb-1.5">
                  Sales Deal Owner <span className="text-error">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select one or multiple employees. Incentive will be split among selected owners.
                </p>
                <MultiSelectEmployee
                  employees={employees}
                  selectedIds={selectedEmployees?.map(e => e?.id)}
                  onChange={handleEmployeesChange}
                  placeholder="Select Sales Deal Owner(s) from Employee Pool..."
                  loading={loadingEmployees}
                />
              </div>

              {/* Incentive Split Config — shown when employees are selected */}
              {selectedEmployees?.length > 0 && finalMargin > 0 && (
                <div>
                  <label className="text-sm font-semibold text-foreground block mb-2">
                    Sales Incentive Split (2% of Net Profit = {formatOMR(salesIncentive)})
                  </label>
                  <IncentiveSplitConfig
                    employees={selectedEmployees}
                    incentiveAmount={salesIncentive}
                    splitConfig={splitConfig}
                    onChange={setSplitConfig}
                  />
                </div>
              )}

              {/* Won Date */}
              <div>
                <label className="text-sm font-semibold text-foreground block mb-2">
                  Won Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={form?.wonDate}
                  onChange={e => setForm(prev => ({ ...prev, wonDate: e?.target?.value }))}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {/* PO / LPO Reference */}
              <div>
                <label className="text-sm font-semibold text-foreground block mb-2">PO / LPO Reference</label>
                <input
                  type="text"
                  value={form?.clientPoReference}
                  onChange={e => setForm(prev => ({ ...prev, clientPoReference: e?.target?.value }))}
                  placeholder="Enter PO or LPO reference number..."
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {/* Final Values */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-foreground block mb-2">Final Project Value (OMR)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={form?.finalProjectValue}
                    onChange={e => setForm(prev => ({ ...prev, finalProjectValue: e?.target?.value }))}
                    placeholder="0.000"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-foreground block mb-2">
                    Final Target Margin / Net Profit (OMR) <span className="text-error">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    value={form?.finalTargetMarginAmount}
                    onChange={e => setForm(prev => ({ ...prev, finalTargetMarginAmount: e?.target?.value }))}
                    placeholder="0.000"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>

              {/* Incentive Preview — full breakdown */}
              {finalMargin > 0 && (
                <div className="bg-gradient-to-br from-success/5 to-emerald-500/5 border border-success/20 rounded-xl p-4">
                  <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Incentive Preview</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Net Profit (Base):</span>
                      <span className="font-semibold text-foreground">{formatOMR(finalMargin)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Sales Incentive (2%)
                        {selectedEmployees?.length > 1 && (
                          <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            {selectedEmployees?.length} owners · {splitConfig?.method === 'equal' ? 'Equal' : 'Custom'} split
                          </span>
                        )}
                        :
                      </span>
                      <span className="font-bold text-success">{formatOMR(salesIncentive)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Project Team Incentive (2%):</span>
                      <span className="font-bold text-success">{formatOMR(projectIncentive)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-success/20">
                      <span className="font-semibold text-foreground">Total Incentives:</span>
                      <span className="font-bold text-primary">{formatOMR(salesIncentive + projectIncentive)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Profit After Incentives:</span>
                      <span className="font-semibold text-foreground">{formatOMR(finalMargin - salesIncentive - projectIncentive)}</span>
                    </div>
                  </div>

                  {/* Per-employee breakdown when multiple selected */}
                  {selectedEmployees?.length > 1 && isSplitValid() && (
                    <div className="mt-3 pt-3 border-t border-success/20">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Sales Incentive Per Owner:</p>
                      <div className="space-y-1">
                        {selectedEmployees?.map(emp => {
                          let pct, amount;
                          if (splitConfig?.method === 'equal') {
                            pct = 100 / selectedEmployees?.length;
                            amount = salesIncentive / selectedEmployees?.length;
                          } else {
                            pct = parseFloat(splitConfig?.shares?.[emp?.id]) || 0;
                            amount = salesIncentive * (pct / 100);
                          }
                          return (
                            <div key={emp?.id} className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{emp?.fullName} ({pct?.toFixed(1)}%)</span>
                              <span className="font-semibold text-success">{formatOMR(amount)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Deal Notes */}
              <div>
                <label className="text-sm font-semibold text-foreground block mb-2">Deal Notes</label>
                <textarea
                  value={form?.dealNotes}
                  onChange={e => setForm(prev => ({ ...prev, dealNotes: e?.target?.value }))}
                  placeholder="Add any notes about this deal..."
                  rows={3}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!existingTicket && !checkingTicket && (
          <div className="p-6 border-t border-border flex items-center justify-end gap-3">
            <Button variant="outline" onClick={() => onClose(null)} disabled={submitting}>Cancel</Button>
            <Button
              variant="default"
              iconName={submitting ? 'Loader2' : 'Trophy'}
              iconPosition="left"
              onClick={handleSubmit}
              disabled={
                submitting ||
                selectedEmployees?.length === 0 ||
                !form?.wonDate ||
                !(finalMargin > 0) ||
                !isSplitValid()
              }
              className="bg-gradient-to-r from-success to-emerald-600 hover:from-success/90 hover:to-emerald-600/90"
            >
              {submitting ? 'Creating...' : 'Mark as Close Won & Create Incentive Ticket'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CloseWonModal;
